import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hand, Bell, X, ArrowRight } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * ActionNeededBanner — a loud, action-inviting strip shown to INTERNAL agents when there
 * are tickets waiting to be TAKEN. Two separate buckets (kept apart on purpose):
 *   • "sin tomar"     — standard open tickets with no assignee (anyone should grab one).
 *   • "sin accionar"  — active monitor-origin notification tickets nobody has taken yet.
 * Data: GET /tickets/action-needed (company-scoped server-side). Not for the client portal.
 *
 * Behaviour: polls every 45s. Dismiss (X) hides it for the session, but if MORE items pile
 * up than when it was dismissed, it re-appears (a real backlog can't be permanently silenced).
 */
export default function ActionNeededBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ unassigned: 0, notifications: 0 });
  const [dismissedAt, setDismissedAt] = useState(() => {
    const v = parseInt(sessionStorage.getItem('actionNeededDismissedAt') || '', 10);
    return Number.isFinite(v) ? v : -1;
  });
  const isInternal = user && user.role !== 'client';

  const fetchCounts = useCallback(async () => {
    try {
      const { data: d } = await api.get('/tickets/action-needed');
      setData({ unassigned: d?.unassigned?.count || 0, notifications: d?.notifications?.count || 0 });
    } catch { /* keep last */ }
  }, []);

  useEffect(() => {
    if (!isInternal) return undefined;
    fetchCounts();
    const id = setInterval(fetchCounts, 45000);
    return () => clearInterval(id);
  }, [isInternal, fetchCounts]);

  if (!isInternal) return null;
  const total = data.unassigned + data.notifications;
  if (total === 0) return null;
  // Dismissed and no NEW backlog since → stay hidden.
  if (dismissedAt >= 0 && total <= dismissedAt) return null;

  const dismiss = () => {
    sessionStorage.setItem('actionNeededDismissedAt', String(total));
    setDismissedAt(total);
  };

  // 20 clics en 34 días murieron en el fondo de la barra: gente que le clickea al
  // cartel esperando que haga algo. Si algo parece un botón tiene que comportarse
  // como un botón, así que el cuerpo entero lleva a la lista que corresponde. Los
  // chips y la X son <button> y se manejan solos — por eso el closest('button').
  const openList = (e) => {
    if (e.target.closest('button')) return;
    navigate(data.unassigned > 0
      ? '/tickets?assigned_to=__unassigned__&status=__active__'
      : '/notifications');
  };

  const parts = [];
  if (data.unassigned > 0) parts.push(
    <button
      key="u"
      type="button"
      onClick={() => navigate('/tickets?assigned_to=__unassigned__&status=__active__')}
      className="action-needed-chip"
    >
      <Hand size={14} />
      <b>{data.unassigned}</b> {t('actionNeeded.unassigned')}
      <ArrowRight size={13} />
    </button>
  );
  if (data.notifications > 0) parts.push(
    <button
      key="n"
      type="button"
      onClick={() => navigate('/notifications')}
      className="action-needed-chip action-needed-chip-amber"
    >
      <Bell size={14} />
      <b>{data.notifications}</b> {t('actionNeeded.notifications')}
      <ArrowRight size={13} />
    </button>
  );

  return (
    <div
      className="action-needed-banner"
      role="status"
      onClick={openList}
      title={t('actionNeeded.openList')}
    >
      <style>{`
        .action-needed-banner{display:flex;align-items:center;gap:12px;flex-wrap:wrap;
          padding:8px 16px;cursor:pointer;background:linear-gradient(90deg,rgba(239,68,68,.14),rgba(245,158,11,.10));
          border-bottom:1px solid var(--c-border);position:relative;overflow:hidden}
        .action-needed-banner::before{content:'';position:absolute;inset:0;
          background:linear-gradient(90deg,transparent,rgba(239,68,68,.18),transparent);
          transform:translateX(-100%);animation:actionNeededSweep 3.2s ease-in-out infinite}
        @keyframes actionNeededSweep{0%{transform:translateX(-100%)}60%,100%{transform:translateX(100%)}}
        @media (prefers-reduced-motion: reduce){.action-needed-banner::before{animation:none;display:none}}
        .action-needed-title{display:flex;align-items:center;gap:8px;font-weight:700;font-size:13.5px;
          color:var(--c-text);z-index:1}
        .action-needed-pulse{width:9px;height:9px;border-radius:50%;background:#ef4444;
          box-shadow:0 0 0 0 rgba(239,68,68,.6);animation:actionNeededPulse 1.6s infinite}
        @keyframes actionNeededPulse{0%{box-shadow:0 0 0 0 rgba(239,68,68,.55)}70%{box-shadow:0 0 0 8px rgba(239,68,68,0)}100%{box-shadow:0 0 0 0 rgba(239,68,68,0)}}
        .action-needed-chips{display:flex;align-items:center;gap:8px;flex-wrap:wrap;z-index:1}
        .action-needed-chip{display:inline-flex;align-items:center;gap:6px;cursor:pointer;
          padding:5px 12px;border-radius:999px;border:1.5px solid #ef4444;background:var(--c-surface);
          color:var(--c-text);font-size:13px;transition:all .12s}
        .action-needed-chip:hover{background:#ef4444;color:#fff}
        .action-needed-chip-amber{border-color:#f59e0b}
        .action-needed-chip-amber:hover{background:#f59e0b}
        .action-needed-chip b{font-size:14px}
        .action-needed-x{margin-left:auto;z-index:1;background:transparent;border:0;cursor:pointer;
          color:var(--c-text-muted);display:flex;padding:4px;border-radius:6px}
        .action-needed-x:hover{background:var(--c-surface-2)}
      `}</style>
      <span className="action-needed-title">
        <span className="action-needed-pulse" aria-hidden="true" />
        {t('actionNeeded.title')}
      </span>
      <div className="action-needed-chips">{parts}</div>
      <button type="button" className="action-needed-x" onClick={dismiss} aria-label={t('common.close')} title={t('common.close')}>
        <X size={16} />
      </button>
    </div>
  );
}
